import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import { WanConnections } from '../../../lib/utils/helpers';
import WanConnectionBox from '../WanConnectionBox';

import JoinNetworkModal from './JoinNetworkModal';
import NetworkList from './NetworkList';

const WifiSettings = ({
  securityModes,
  knownNetworks,
  otherNetworks,
  active,
  onStatusChange,
  onJoinNetwork,
  loading = false,
  readOnly = false,
  children,
  connectionStatus,
}) => {
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const [modal, setJoinNetworkModal] = useState(false);

  const connectNetwork = (network) => {
    const { ssid, security } = network;
    setSelectedNetwork(network);
    onJoinNetwork({ ssid, security });
  };

  const addOtherNetwork = (network) => {
    setSelectedNetwork(network);
    setJoinNetworkModal(true);
  };

  const handleOtherNetwork = (network) => {
    const { ssid, security, passphrase } = network;
    onJoinNetwork({ ssid, security, passphrase });
    setJoinNetworkModal(false);
  };

  const openSecurityModes = securityModes.reduce((result, mode) => {
    return mode.requiresPassphrase
      ? result
      : [...result, mode.name.toUpperCase()];
  }, []);

  const activeNetwork = knownNetworks.find((network) => network.connected);

  return (
    <>
      <Row>
        <Col xl={4}>
          <WanConnectionBox
            active={active}
            type="wifi"
            connectionStatus={connectionStatus}
            networkName={activeNetwork?.ssid || ''}
            onStatusChange={onStatusChange}
            readOnly={readOnly}
            signalStrength={activeNetwork?.signalStrengthNormalized}
          />
        </Col>
        {active && (
          <Col xl={8}>
            {children && (
              <div className="d-block d-sm-none mt-4 text-right">
                {children}
              </div>
            )}
            <div
              className="mt-3 mt-xl-0 d-flex justify-content-between"
              style={{ lineHeight: '2.27rem' }}
            >
              <strong>Preferred Networks</strong>
              {children && (
                <span className="d-none d-sm-block">{children}</span>
              )}
            </div>

            <NetworkList
              networks={knownNetworks}
              readOnly={readOnly}
              loading={loading}
              selectedSsid={selectedNetwork.ssid}
              openSecurityModes={openSecurityModes}
              onConnectNetwork={connectNetwork}
            />

            <div>
              <div className="mt-4" style={{ lineHeight: '2.27rem' }}>
                <strong>Other Networks</strong>
              </div>

              <NetworkList
                networks={otherNetworks}
                readOnly={readOnly}
                loading={loading}
                selectedSsid={selectedNetwork.ssid}
                openSecurityModes={openSecurityModes}
                onConnectNetwork={(network) =>
                  openSecurityModes.includes(network.security.toUpperCase())
                    ? connectNetwork(network)
                    : addOtherNetwork(network)
                }
              />
            </div>
          </Col>
        )}
        {!active && (
          <Col
            sm={8}
            className="align-items-lg-center d-flex justify-content-center"
          >
            <strong>Please turn on Wi-Fi to view available networks.</strong>
          </Col>
        )}
      </Row>
      {active && modal && (
        <JoinNetworkModal
          modal={modal}
          network={selectedNetwork}
          onHide={setJoinNetworkModal}
          onJoin={handleOtherNetwork}
          securityModes={securityModes}
        />
      )}
    </>
  );
};

const networkShape = {
  ssid: PropTypes.string.isRequired,
  connected: PropTypes.bool.isRequired,
  security: PropTypes.string.isRequired,
  signalStrengthNormalized: PropTypes.number.isRequired,
};

WifiSettings.propTypes = {
  securityModes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      requiresPassphrase: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  knownNetworks: PropTypes.arrayOf(PropTypes.shape(networkShape)),
  otherNetworks: PropTypes.arrayOf(PropTypes.shape(networkShape)),
  loading: PropTypes.bool,
  onJoinNetwork: PropTypes.func,
  onStatusChange: PropTypes.func,
  active: PropTypes.bool,
  readOnly: PropTypes.bool,
  children: PropTypes.node,
  connectionStatus: PropTypes.oneOf(['', ...WanConnections]),
};

export default WifiSettings;
